import { useCallback, useEffect, useState } from "react";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import { Section, Table, Td, Badge, Tile, LinkPill, Banner } from "../components/ui";

// Shape mirrors the admin_system_health edge fn. Each section degrades
// independently: { ok:true, rows } or { ok:false, error }.
type Section<T> = { ok: true; rows: T[] } | { ok: false; error: string };

interface HealthReport {
  generated_at: string;
  database: { ok: boolean; latency_ms?: number; error?: string };
  keys: { edge_service_secret: boolean; edge_publishable_key: boolean };
  legacy_keys_disabled: boolean;
  content: Section<{
    events_total: number | string;
    events_embedded: number | string;
    places_total: number | string;
    places_embedded: number | string;
    users_total: number | string;
  }>;
  ingestion: Section<{
    last_event_created: string | null;
    last_place_created: string | null;
    events_24h: number | string;
    events_7d: number | string;
    places_24h: number | string;
    places_7d: number | string;
  }>;
  // LLM extraction health by source (website vs Instagram), from admin.ai_invocations.
  extraction: Section<{
    source_type: string;
    attempts_24h: number | string;
    errors_24h: number | string;
    attempts_7d: number | string;
    errors_7d: number | string;
    cost_24h: number | string | null;
    last_at: string | null;
  }>;
  webhooks_summary: Section<{
    total: number | string;
    ok_2xx: number | string;
    errors: number | string;
    timed_out: number | string;
  }>;
  webhooks_recent: Section<{
    id: number;
    status_code: number | null;
    error_msg: string | null;
    timed_out: boolean;
    created: string;
  }>;
  cron_jobs: Section<{ jobid: number; jobname: string | null; schedule: string; active: boolean }>;
  cron_runs: Section<{
    jobid: number;
    jobname: string | null;
    status: string;
    start_time: string;
    end_time: string | null;
    return_message: string;
  }>;
}

const PROJECT = "gyaftaczfhgxjmomwnwz";
const SUPA = `https://supabase.com/dashboard/project/${PROJECT}`;

// pg returns bigint count(*) as a string; coerce for display/math.
const num = (v: unknown): number => (v == null ? 0 : Number(v));
const pct = (part: unknown, total: unknown): number => {
  const t = num(total);
  return t === 0 ? 100 : Math.round((num(part) / t) * 100);
};
const fmt = (iso: string | null): string => (iso ? new Date(iso).toLocaleString() : "—");

// A report section may be ABSENT (not merely { ok:false }) when the DEPLOYED
// admin_system_health edge fn is OLDER than this SPA — e.g. a v1 backend that
// predates a newly added section like `extraction`. Coerce a missing section to
// a soft error card so one absent key can't throw `report.x.ok` and (with no
// error boundary) white-screen the whole console. Deployed-edge ⇄ SPA skew must
// DEGRADE, not crash.
function sec<T>(s: Section<T> | undefined): Section<T> {
  return s ?? { ok: false, error: "unavailable — backend may be older than the console" };
}

export default function SystemHealth() {
  const [report, setReport] = useState<HealthReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await invokeAdmin<HealthReport>("admin_system_health"));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- mount fetch; loader's sync setLoading/setError reset is intentional (shared with Refresh); data state is only written after the awaited request
    fetchHealth();
  }, [fetchHealth]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">System Health</h1>
          <p className="text-sm text-zinc-500">
            Live infrastructure status across the database, edge keys, webhooks, and scheduled jobs.
            {report && (
              <span className="ml-1 text-zinc-600">Updated {fmt(report.generated_at)}.</span>
            )}
          </p>
        </div>
        <button
          onClick={fetchHealth}
          disabled={loading}
          className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && <Banner className="mt-3">{error}</Banner>}

      {!report && loading && <p className="mt-6 text-sm text-zinc-500">Loading…</p>}

      {report && (() => {
        // Guard every section/object — a key may be absent on an older deployed
        // backend (see `sec`). Without this, one missing section throws and
        // unmounts the whole SPA (the System Health "crash").
        const db = report.database ?? { ok: false as const, error: "unavailable" };
        const keys = report.keys ?? { edge_service_secret: false, edge_publishable_key: false };
        const legacyDisabled = report.legacy_keys_disabled ?? false;
        const content = sec(report.content);
        const ingestion = sec(report.ingestion);
        const extraction = sec(report.extraction);
        const webhooksSummary = sec(report.webhooks_summary);
        const webhooksRecent = sec(report.webhooks_recent);
        const cronJobs = sec(report.cron_jobs);
        const cronRuns = sec(report.cron_runs);
        return (
        <>
          {/* API & Database */}
          <Section title="API & Database">
            <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
              <Tile
                label="Database"
                ok={db.ok}
                value={db.ok ? `${db.latency_ms ?? "?"} ms` : "down"}
                sub={db.error}
              />
              <Tile
                label="Edge secret key"
                ok={keys.edge_service_secret}
                value={keys.edge_service_secret ? "present" : "missing"}
              />
              <Tile
                label="Edge publishable key"
                ok={keys.edge_publishable_key}
                value={keys.edge_publishable_key ? "present" : "missing"}
              />
              <Tile
                label="Legacy API keys"
                ok={legacyDisabled}
                value={legacyDisabled ? "disabled" : "ENABLED"}
                sub={legacyDisabled ? "good — disabled at the gateway" : undefined}
              />
            </div>
          </Section>

          {/* Content & embeddings */}
          <Section title="Content & Embeddings" err={!content.ok ? content.error : undefined}>
            {content.ok && content.rows[0] && (
              <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-3">
                {(() => {
                  const c = content.rows[0];
                  const ev = pct(c.events_embedded, c.events_total);
                  const pl = pct(c.places_embedded, c.places_total);
                  return (
                    <>
                      <Tile label="Users" ok value={num(c.users_total).toLocaleString()} />
                      <Tile
                        label="Events embedded"
                        ok={ev >= 95}
                        value={`${ev}%`}
                        sub={`${num(c.events_embedded).toLocaleString()} / ${num(c.events_total).toLocaleString()}`}
                      />
                      <Tile
                        label="Places embedded"
                        ok={pl >= 95}
                        value={`${pl}%`}
                        sub={`${num(c.places_embedded).toLocaleString()} / ${num(c.places_total).toLocaleString()}`}
                      />
                    </>
                  );
                })()}
              </div>
            )}
          </Section>

          {/* Ingestion */}
          <Section title="Ingestion" err={!ingestion.ok ? ingestion.error : undefined}>
            {ingestion.ok && ingestion.rows[0] && (
              <div className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
                {(() => {
                  const i = ingestion.rows[0];
                  return (
                    <>
                      <Tile label="Last event ingested" ok value={fmt(i.last_event_created)} />
                      <Tile label="Last place ingested" ok value={fmt(i.last_place_created)} />
                      <Tile label="Events (24h / 7d)" ok value={`${num(i.events_24h)} / ${num(i.events_7d)}`} />
                      <Tile label="Places (24h / 7d)" ok value={`${num(i.places_24h)} / ${num(i.places_7d)}`} />
                    </>
                  );
                })()}
              </div>
            )}
          </Section>

          {/* Extraction (LLM) by source */}
          <Section
            title="Extraction (LLM, by source — 24h)"
            err={!extraction.ok ? extraction.error : undefined}
          >
            {extraction.ok && (
              <Table head={["Source", "Attempts", "Errors", "Est. cost", "Last"]}>
                {extraction.rows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-2 text-zinc-500" colSpan={5}>
                      No extractions recorded yet.
                    </td>
                  </tr>
                ) : (
                  extraction.rows.map((r) => {
                    const errs = num(r.errors_24h);
                    const attempts = num(r.attempts_24h);
                    return (
                      <tr key={r.source_type} className="border-t border-zinc-800">
                        <Td className="text-zinc-300">{r.source_type}</Td>
                        <Td className="text-zinc-400">{attempts}</Td>
                        <Td>
                          <Badge ok={errs === 0}>{errs}</Badge>
                        </Td>
                        <Td className="text-zinc-400">
                          {r.cost_24h == null ? "—" : `$${num(r.cost_24h).toFixed(4)}`}
                        </Td>
                        <Td mono className="text-zinc-400">{fmt(r.last_at)}</Td>
                      </tr>
                    );
                  })
                )}
              </Table>
            )}
          </Section>

          {/* Webhooks (pg_net) */}
          <Section
            title="DB Webhooks (pg_net, last 24h)"
            err={!webhooksSummary.ok ? webhooksSummary.error : undefined}
          >
            {webhooksSummary.ok && (
              <div className="grid grid-cols-2 gap-3 p-3 sm:grid-cols-4">
                {(() => {
                  const w = webhooksSummary.rows[0] ?? { total: 0, ok_2xx: 0, errors: 0, timed_out: 0 };
                  return (
                    <>
                      <Tile label="Deliveries" ok value={num(w.total).toString()} />
                      <Tile label="2xx" ok value={num(w.ok_2xx).toString()} />
                      <Tile label="Errors" ok={num(w.errors) === 0} value={num(w.errors).toString()} />
                      <Tile label="Timed out" ok={num(w.timed_out) === 0} value={num(w.timed_out).toString()} />
                    </>
                  );
                })()}
              </div>
            )}
          </Section>

          {webhooksRecent.ok && webhooksRecent.rows.length > 0 && (
            <Section title="Recent webhook deliveries">
              <Table head={["When", "Status", "Timed out", "Error"]}>
                {webhooksRecent.rows.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-800">
                    <Td mono className="text-zinc-400">{fmt(r.created)}</Td>
                    <Td>
                      <Badge ok={!!r.status_code && r.status_code < 400 && !r.timed_out}>
                        {r.status_code ?? "—"}
                      </Badge>
                    </Td>
                    <Td className="text-zinc-400">{r.timed_out ? "yes" : "no"}</Td>
                    <Td className="text-zinc-500">{r.error_msg ?? "—"}</Td>
                  </tr>
                ))}
              </Table>
            </Section>
          )}

          {/* pg_cron */}
          <Section title="Scheduled jobs (pg_cron)" err={!cronJobs.ok ? cronJobs.error : undefined}>
            {cronJobs.ok && (
              <Table head={["Job", "Schedule", "Active"]}>
                {cronJobs.rows.length === 0 ? (
                  <tr>
                    <Td className="text-zinc-500">No cron jobs configured.</Td>
                    <Td>{""}</Td>
                    <Td>{""}</Td>
                  </tr>
                ) : (
                  cronJobs.rows.map((j) => (
                    <tr key={j.jobid} className="border-t border-zinc-800">
                      <Td className="text-zinc-300">{j.jobname ?? `job ${j.jobid}`}</Td>
                      <Td mono className="text-zinc-400">{j.schedule}</Td>
                      <Td>
                        <Badge ok={j.active}>{j.active ? "active" : "paused"}</Badge>
                      </Td>
                    </tr>
                  ))
                )}
              </Table>
            )}
          </Section>

          {cronRuns.ok && cronRuns.rows.length > 0 && (
            <Section title="Recent job runs">
              <Table head={["Job", "Status", "Started", "Message"]}>
                {cronRuns.rows.map((r, i) => (
                  <tr key={`${r.jobid}-${i}`} className="border-t border-zinc-800">
                    <Td className="text-zinc-300">{r.jobname ?? `job ${r.jobid}`}</Td>
                    <Td>
                      <Badge ok={r.status === "succeeded"}>{r.status}</Badge>
                    </Td>
                    <Td mono className="text-zinc-400">{fmt(r.start_time)}</Td>
                    <Td className="text-zinc-500">{r.return_message || "—"}</Td>
                  </tr>
                ))}
              </Table>
            </Section>
          )}

          {/* External dashboards */}
          <Section title="External dashboards">
            <div className="flex flex-wrap gap-2 p-3 text-sm">
              <LinkPill href={`${SUPA}/advisors/security`}>Supabase advisors</LinkPill>
              <LinkPill href={`${SUPA}/logs/edge-functions`}>Edge function logs</LinkPill>
              <LinkPill href={`${SUPA}/database/postgres-logs`}>Postgres logs</LinkPill>
              <LinkPill href="/logs">CloudWatch &amp; Sentry (in-app)</LinkPill>
            </div>
          </Section>
        </>
        );
      })()}
    </div>
  );
}

// Presentational helpers (Tile/Section/Table/Td/Badge/Banner/LinkPill) live in
// ../components/ui.
